import React, { useRef, useMemo, useEffect, useState, Suspense } from 'react'
import { Canvas, useFrame } from '@react-three/fiber'
import * as THREE from 'three'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

const VOXEL_SIZE = 0.35; // Size of each cube

// Colors from the image
const COLORS = {
  core: '#4d2a78', // Dark purple/blue base
  coreHighlight: '#5e3494', // Lighter purple for layering
  coreDark: '#3b205d', // Darker purple
  mantle: '#7d3c98', // Lighter re-purple layer
  mantleDark: '#6a2c85', // Darker mantle
  surface: '#F98B02', // Orange
  surfaceHighlight: '#ffb84d', // Lighter orange
  surfaceDark: '#d67600', // Darker orange
  water: '#00a6ed', // Blue
  waterLight: '#33bfff', // Lighter blue
  treeTrunk: '#0096a6', // Cyan dark
  treeTop: '#00d2e6', // Cyan light
  treeTopHighlight: '#4ff0ff', // Lighter cyan
  rock: '#b81d78', // Pink/Purple
  rockHighlight: '#d63b96', // Lighter pink
  rockDark: '#961660', // Darker pink
};

function VoxelField() {
  const meshRef = useRef()

  // Generate voxels
  const { positions, colors, count } = useMemo(() => {
    const pos = []
    const col = []
    const colorHelper = new THREE.Color()

    const radius = 17;

    // Helper to add voxel
    const addVoxel = (x, y, z, colorHex) => {
      pos.push(x, y, z)
      colorHelper.set(colorHex)
      col.push(colorHelper.r, colorHelper.g, colorHelper.b)
    }

    // 1. Generate Base Planet (Hemisphere-ish)
    for (let x = -radius; x <= radius; x++) {
      for (let z = -radius; z <= radius; z++) {
        const dist = Math.sqrt(x * x + z * z);

        if (dist < radius) {
          // Depth of the planet body
          const depth = Math.sqrt(radius * radius - dist * dist);
          const bottomY = -Math.floor(depth * 0.9);
          const topY = 0;

          for (let y = bottomY; y <= topY; y++) {
            let color = COLORS.core;

            // Add some texture to the core
            const noise = Math.random();
            if (y < -5) {
              if (noise > 0.8) color = COLORS.coreHighlight;
              else if (noise < 0.2) color = COLORS.coreDark;
            }

            // Mantle Layer (between core and surface)
            if (y >= -5 && y < -1) {
              color = COLORS.mantle;
              if (noise > 0.7) color = COLORS.mantleDark;

              // Dither with core at the bottom of mantle
              if (y === -5 && noise < 0.4) color = COLORS.core;
            }

            // Surface layer
            if (y === topY) {
              color = COLORS.surface;
              // Random surface variation
              if (noise > 0.7) color = COLORS.surfaceHighlight;
              else if (noise < 0.1) color = COLORS.surfaceDark;
            } else if (y === topY - 1) {
              // Dithering transition between Surface and Mantle
              if (noise > 0.5) color = COLORS.surface;
              else color = COLORS.mantle;
            }

            // Don't place surface voxels where we want water
            // Let's define water pools
            const isWaterPool1 = (Math.abs(x - 5) < 3 && Math.abs(z + 5) < 3);
            const isWaterPool2 = (Math.abs(x + 3) < 3 && Math.abs(z - 3) < 3);
            const isWater = y === topY && (isWaterPool1 || isWaterPool2);

            if (isWater) {
              color = COLORS.water;
              if (Math.random() > 0.8) color = COLORS.waterLight;
            }

            addVoxel(x, y, z, color);

            // Add surface bumps
            if (y === topY && !isWater) {
              // 15% chance of a bump
              if (Math.random() > 0.85) {
                addVoxel(x, y + 1, z, color);
              }
            }
          }
        }
      }
    }

    // 2. Add Features

    // Trees (Cyan) - Left side
    const addTree = (bx, bz, height) => {
      // Trunk
      for (let h = 1; h <= height; h++) {
        addVoxel(bx, h, bz, COLORS.treeTrunk);
        // Thicker trunk base
        if (h < 3) {
          addVoxel(bx + 1, h, bz, COLORS.treeTrunk);
          addVoxel(bx, h, bz + 1, COLORS.treeTrunk);
          addVoxel(bx + 1, h, bz + 1, COLORS.treeTrunk);
        }
      }
      // Leaves (Sphere-ish top)
      const topY = height + 1;
      const leafRadius = 3;
      for (let lx = -leafRadius; lx <= leafRadius; lx++) {
        for (let ly = -leafRadius; ly <= leafRadius; ly++) {
          for (let lz = -leafRadius; lz <= leafRadius; lz++) {
            if (lx * lx + ly * ly + lz * lz < leafRadius * leafRadius) {
              let color = COLORS.treeTop;
              if (Math.random() > 0.8) color = COLORS.treeTopHighlight;
              addVoxel(bx + lx, topY + ly, bz + lz, color);
            }
          }
        }
      }
    }

    addTree(-6, -5, 8);
    addTree(-10, 2, 6);

    // Mountain/Rock Formation (Pink/Purple) - Right/Back side
    const addMountain = (bx, bz) => {
      // Create a jagged peak
      const peakHeight = 12;
      for (let y = 1; y <= peakHeight; y++) {
        // Tapering as we go up
        const layerRadius = Math.max(1, 6 - Math.floor(y / 2));

        for (let lx = -layerRadius; lx <= layerRadius; lx++) {
          for (let lz = -layerRadius; lz <= layerRadius; lz++) {
            // Randomize shape
            if (Math.abs(lx) + Math.abs(lz) <= layerRadius + (Math.random() * 2)) {
              // Offset from base
              const vx = bx + lx;
              const vz = bz + lz;

              // Only place if within bounds (roughly)
              if (vx * vx + vz * vz < radius * radius + 20) {
                let color = COLORS.rock;
                const noise = Math.random();
                if (y > 5) {
                  if (noise > 0.7) color = COLORS.rockHighlight;
                  else if (noise < 0.2) color = COLORS.rockDark;
                }
                addVoxel(vx, y, vz, color);
              }
            }
          }
        }
      }
    }

    addMountain(6, 6);

    // Small scattered rocks
    addVoxel(-5, 1, 8, COLORS.rock);
    addVoxel(2, 1, 10, COLORS.rock);
    addVoxel(8, 1, -5, COLORS.rock);
    addVoxel(-8, 1, -8, COLORS.rock);


    return { positions: pos, colors: col, count: pos.length / 3 }
  }, [])

  useEffect(() => {
    if (!meshRef.current) return;

    const tempObject = new THREE.Object3D()
    const color = new THREE.Color()

    for (let i = 0; i < count; i++) {
      const x = positions[i * 3]
      const y = positions[i * 3 + 1]
      const z = positions[i * 3 + 2]

      tempObject.position.set(x * VOXEL_SIZE, y * VOXEL_SIZE, z * VOXEL_SIZE)
      tempObject.updateMatrix()
      meshRef.current.setMatrixAt(i, tempObject.matrix)

      color.setRGB(colors[i * 3], colors[i * 3 + 1], colors[i * 3 + 2])
      meshRef.current.setColorAt(i, color)
    }
    meshRef.current.instanceMatrix.needsUpdate = true
    if (meshRef.current.instanceColor) meshRef.current.instanceColor.needsUpdate = true
  }, [positions, colors, count])


  useEffect(() => {
    if (!meshRef.current) return;

    const ctx = gsap.context(() => {
      // Rotate based on scroll
      // Full page scroll = 4 full rotations (adjust multiplier as needed for sensitivity)
      gsap.to(meshRef.current.rotation, {
        y: Math.PI * 0.5,
        ease: "none",
        scrollTrigger: {
          trigger: document.body,
          start: "top top",
          end: "bottom bottom",
          scrub: 1, // Smooth scrubbing
        }
      });
    });

    return () => ctx.revert();
  }, []);

  useFrame((state) => {
    if (meshRef.current) {
      // meshRef.current.rotation.y += 0.00125 // Removed constant rotation
      // Add a slight floating bob
      meshRef.current.position.y = Math.sin(state.clock.elapsedTime * 0.5) * 0.2
    }
  })

  return (
    <instancedMesh ref={meshRef} args={[null, null, count]}>
      <boxGeometry args={[VOXEL_SIZE, VOXEL_SIZE, VOXEL_SIZE]} />
      <meshStandardMaterial />
    </instancedMesh>
  )
}

export default function PlanetVoxel({ className }) {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkViewport = () => {
      setIsMobile(window.innerWidth < 960);
    };
    checkViewport();
    window.addEventListener('resize', checkViewport);
    return () => window.removeEventListener('resize', checkViewport);
  }, []);

  return (
    <div
      className={`${className} h-full w-full relative transition-opacity duration-1000 ease-in-out`}
      style={{ opacity: isMobile ? 0.8 : 1 }}
    >
      <div className="absolute inset-0 flex justify-center items-center">
        <Suspense fallback={null}>
          <Canvas
            camera={{ position: [10, 7.5, 15], fov: 45 }}
            style={{ position: 'absolute' }}
            gl={{
              antialias: true,
              alpha: true,
            }}
            dpr={[1, 2]}
          >
            <ambientLight intensity={0.7} />
            <directionalLight position={[10, 20, 5]} intensity={1.2} castShadow />
            <pointLight position={[-10, -10, -10]} intensity={0.5} color="#4a00d8" />

            <group position={[0, 0, 0]}>
              <VoxelField />
            </group>
          </Canvas>
        </Suspense>
      </div>
    </div>
  )
}
